import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, resolveMedia } from "@/lib/api";
import { Upload, Trash2, FileText, Video as VideoIcon, GripVertical, Youtube, Link as LinkIcon } from "lucide-react";
import { detectExternalVideo } from "@/lib/videoUrl";

function detectKind(it) {
  if (!it) return "image";
  if (it.type === "video-url" || detectExternalVideo(it.url)) return "video-url";
  if (it.type) return it.type;
  const u = (it.url || "").toLowerCase();
  if (u.match(/\.(mp4|webm|mov)$/)) return "video";
  if (u.endsWith(".pdf")) return "pdf";
  return "image";
}

export default function MediaUpload({ value = [], onChange, testid = "media-upload" }) {
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      const next = [...value];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push({ type: data.kind, url: data.url, preview_url: data.preview_url || "", caption: "" });
      }
      onChange(next);
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  function addVideoUrl() {
    const url = window.prompt("YouTube- oder Vimeo-URL einfügen:");
    if (!url) return;
    const ext = detectExternalVideo(url);
    if (!ext) {
      toast.error("URL nicht erkannt. Bitte eine gültige YouTube- oder Vimeo-Adresse einfügen.");
      return;
    }
    onChange([...value, { type: "video-url", url: url.trim(), caption: "", provider: ext.provider }]);
    toast.success(`${ext.provider === "youtube" ? "YouTube" : "Vimeo"}-Video hinzugefügt`);
  }

  function update(i, patch) {
    const next = [...value];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function remove(i) {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
  }

  // Drag-and-Drop
  function onDragStart(i, e) {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) {}
  }
  function onDragOver(i, e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== i) setOverIdx(i);
  }
  function onDrop(i, e) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...value];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    onChange(next);
    setDragIdx(null);
    setOverIdx(null);
  }
  function onDragEnd() { setDragIdx(null); setOverIdx(null); }

  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50">
          <Upload className="h-3.5 w-3.5" /> {busy ? "Hochladen…" : "Datei hochladen"}
          <input type="file" accept="image/*,video/*,application/pdf" multiple className="hidden" onChange={handleFiles} disabled={busy} />
        </label>
        <button
          type="button"
          onClick={addVideoUrl}
          data-testid={`${testid}-add-video-url`}
          className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-xs hover:bg-neutral-50"
        >
          <Youtube className="h-3.5 w-3.5" /> YouTube/Vimeo URL
        </button>
        <span className="text-xs text-neutral-400">Bilder · Videos · PDFs · max. 25 MB</span>
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-neutral-400 italic">Noch keine Medien hinzugefügt.</p>
      ) : (
        <ul className="space-y-2">
          {value.map((it, i) => {
            const kind = detectKind(it);
            const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
            const isDragged = dragIdx === i;
            return (
              <li
                key={i}
                draggable
                onDragStart={(e) => onDragStart(i, e)}
                onDragOver={(e) => onDragOver(i, e)}
                onDrop={(e) => onDrop(i, e)}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-3 border p-2 bg-white transition-all ${isOver ? "border-[hsl(var(--brand-green-strong))] border-2" : "border-brand-line"} ${isDragged ? "opacity-40" : ""}`}
                data-testid={`media-item-${i}`}
              >
                <span className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-700 px-1" title="Zum Sortieren ziehen">
                  <GripVertical className="h-4 w-4" />
                </span>
                <div className="w-16 h-12 bg-neutral-100 flex items-center justify-center overflow-hidden shrink-0">
                  {kind === "image" ? (
                    <img src={resolveMedia(it.url)} alt="" className="w-full h-full object-cover" />
                  ) : kind === "video-url" ? (
                    (detectExternalVideo(it.url)?.thumb) ? (
                      <img src={detectExternalVideo(it.url).thumb} alt="" className="w-full h-full object-cover" />
                    ) : <LinkIcon className="h-5 w-5 text-neutral-500" />
                  ) : kind === "video" ? (
                    <VideoIcon className="h-5 w-5 text-neutral-500" />
                  ) : (
                    <FileText className="h-5 w-5 text-neutral-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-neutral-500 truncate">
                    <span className="uppercase tracking-wider mr-2">
                      {kind === "video-url" ? (detectExternalVideo(it.url)?.provider || "video") : kind}
                    </span>
                    <span className="text-neutral-400">{it.url}</span>
                  </p>
                  <input
                    type="text"
                    placeholder="Bildunterschrift (optional)"
                    value={it.caption || ""}
                    onChange={(e) => update(i, { caption: e.target.value })}
                    data-testid={`media-caption-${i}`}
                    className="mt-1 w-full text-sm border-0 border-b border-transparent focus:border-brand-line focus:outline-none px-0 py-0.5"
                  />
                </div>
                <button type="button" onClick={() => remove(i)} className="p-1 text-neutral-400 hover:text-red-600 shrink-0" title="Entfernen"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}

      {value.length > 1 && (
        <p className="mt-2 text-[11px] text-neutral-400 italic">
          Tipp: Einträge zum Sortieren am Griff <GripVertical className="inline h-3 w-3 -mt-0.5" /> ziehen. Der erste Eintrag erscheint als großes Galerie-Header.
        </p>
      )}
    </div>
  );
}
